import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';
import AppImage from '../components/AppImage';
import UserAvatar from '../components/ui/UserAvatar';
import { useBranding } from '../components/BrandingProvider';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import EnterpriseCommandPalette from './EnterpriseCommandPalette';
import { useWorkspaceWindows } from './WorkspaceWindowHost';

const TITLES = {
  '/dashboard': 'Dashboard',
  '/profile-center': 'Perfil',
  '/mi-portal': 'Mi Portal',
  '/admin/employees': 'Empleados',
  '/admin/attendance': 'Asistencia',
  '/admin/nomina': 'Nómina',
  '/admin/vacaciones': 'Vacaciones',
  '/admin/permisos': 'Permisos',
  '/finanzas': 'Finanzas',
  '/admin/obras-financiero': 'Control financiero',
  '/admin/catalogos': 'Catálogos',
  '/admin/compras': 'Compras',
  '/admin/inventario': 'Inventario',
  '/admin/caja': 'Caja general',
  '/admin/tesoreria/agenda-cheques': 'Agenda de cheques',
  '/admin/tiempo-extra': 'Tiempo extra',
  '/admin/incidents': 'Incidentes',
  '/admin/reports': 'Reportes',
  '/admin/system': 'Sistema',
  '/admin/roles': 'Roles y permisos',
  '/help': 'Centro de Ayuda',
};

export default function EnterpriseTopbar({ userRole, onMobileMenu }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { theme, toggleTheme } = useTheme();
  const { userProfile, user } = useAuth();
  const { windows } = useWorkspaceWindows();
  const assistantAllowed = ['admin', 'superadmin'].includes(userRole);
  const title =
    TITLES[location.pathname] ||
    Object.entries(TITLES).find(([path]) =>
      location.pathname.startsWith(`${path}/`)
    )?.[1] ||
    'Nova ERP';

  return (
    <header className="cp07-topbar cp08-topbar z-30 flex h-12 shrink-0 items-center gap-2 px-3 sm:px-3.5">
      <button
        type="button"
        onClick={onMobileMenu}
        className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden"
        aria-label="Abrir menú"
      >
        <Icon name="Menu" size={19} />
      </button>

      <div className="flex min-w-0 items-center gap-2 md:hidden">
        <div className="h-7 w-7 overflow-hidden rounded-md bg-slate-100">
          <AppImage
            src={branding?.logo_url || '/assets/images/logo.png'}
            alt="Logo"
            className="h-full w-full object-contain"
          />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">{title}</p>
        <p className="hidden truncate text-[10px] text-slate-500 sm:block">
          {branding?.nombre_empresa || 'Nova ERP'}
        </p>
      </div>

      {windows.length > 0 && (
        <div className="cp07-window-count hidden items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-medium sm:flex">
          <Icon name="PanelsTopLeft" size={14} />
          {windows.length} {windows.length === 1 ? 'ventana' : 'ventanas'}
        </div>
      )}

      {assistantAllowed && (
        <button
          type="button"
          onClick={() =>
            window.dispatchEvent(new CustomEvent('nova:assistant-toggle'))
          }
          className="cp07-assistant-button inline-flex h-8 items-center gap-1.5 rounded-xl px-2.5 text-[11px] font-semibold transition-all"
          aria-label="Abrir Alex AI"
          title="Alex AI"
        >
          <Icon name="Bot" size={14} />
          <span className="hidden xl:inline">Asistente</span>
        </button>
      )}

      <button
        type="button"
        onClick={toggleTheme}
        className="cp07-topbar-action inline-flex h-8 w-8 items-center justify-center rounded-xl transition-all"
        aria-label={
          theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'
        }
        title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
        data-testid="button-theme-toggle"
      >
        <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={15} />
      </button>

      <button
        type="button"
        onClick={() => navigate('/help')}
        className="cp07-topbar-action inline-flex h-8 items-center gap-1.5 rounded-xl px-2.5 text-[11px] font-medium transition-all"
        aria-label="Abrir Centro de Ayuda"
        title="Centro de Ayuda"
      >
        <Icon name="CircleHelp" size={15} />
        <span className="hidden xl:inline">Ayuda</span>
      </button>

      <EnterpriseCommandPalette userRole={userRole} />

      <button
        type="button"
        onClick={() => navigate('/profile-center')}
        className="cp07-user-chip hidden min-w-0 items-center gap-2 rounded-xl px-2 py-1 text-left transition-all lg:flex"
        title="Abrir mi perfil"
      >
        <UserAvatar
          src={userProfile?.avatar_url || user?.user_metadata?.avatar_url}
          name={userProfile?.full_name || user?.email}
          size="sm"
        />
        <span className="min-w-0">
          <span className="block max-w-40 truncate text-[11px] font-semibold text-slate-800">
            {userProfile?.full_name || 'Usuario'}
          </span>
          <span className="block text-[9px] capitalize text-slate-500">
            {userRole}
          </span>
        </span>
      </button>
    </header>
  );
}
